import * as DateF from 'date-fns'

import { Data } from '@blade/std-lib'
import { Period as PeriodLib } from '@root/shared/period'

import type { Period } from '@root/shared/period'

export class AsIs extends Data.TaggedClass('AsIs') {}
export class Day extends Data.TaggedClass('Day')<{ number: number }> {}
export class Hour extends Data.TaggedClass('Hour')<{ number: number }> {}
export class FiveMinute extends Data.TaggedClass('FiveMinute')<{
  number: number
}> {}
export class FifteenMinute extends Data.TaggedClass('FifteenMinute')<{
  number: number
}> {}

export type PnlChartDivision = Day | Hour | FiveMinute | AsIs | FifteenMinute

export const PeriodToLabelMap = PeriodLib.PERIOD_LABEL_MAP

export const PeriodToDivisionMap: Record<Period.Period, PnlChartDivision> = {
  ALL: new AsIs(),
  '7D': new Day({ number: 7 }),
  '24H': new Hour({ number: 24 }),
  '12H': new Hour({ number: 12 }),
  '6H': new FifteenMinute({ number: 6 * 4 }),
  '1H': new FiveMinute({ number: 60 / 5 }),
}

export const fromPeriod = (period: Period.Period) => PeriodToDivisionMap[period]

export const match = Data.matchFor<PnlChartDivision>()

export function roundToFiveMinute(date: Date) {
  return DateF.setMilliseconds(
    DateF.setSeconds(
      DateF.setMinutes(date, Math.floor(date.getMinutes() / 5) * 5),
      0,
    ),
    0,
  )
}
export function roundToFifteenMinute(date: Date) {
  return DateF.setMilliseconds(
    DateF.setSeconds(
      DateF.setMinutes(date, Math.floor(date.getMinutes() / 15) * 15),
      0,
    ),
    0,
  )
}

function generateWithStep({
  dateInPast,
  currentDate,
  number,
  stepFn,
  stepSize = 1,
}: {
  dateInPast: Date
  currentDate: Date
  number: number
  stepFn: (d: Date, step: number) => Date
  stepSize?: number
}): Array<Date> {
  const dates: Array<Date> = []
  let cursor = currentDate

  while (!DateF.isBefore(cursor, dateInPast)) {
    dates.push(cursor)
    cursor = stepFn(cursor, stepSize)
  }

  while (dates.length < number + 1) {
    dates.push(cursor)
    cursor = stepFn(cursor, stepSize)
  }

  return dates
}

export function generateDates(options: {
  division: PnlChartDivision
  currentDate: () => Date
  dateInPast: Date
}): Array<Date> {
  const currentDate = options.currentDate()

  return match(options.division, {
    AsIs: () =>
      generateWithStep({
        dateInPast: DateF.startOfDay(options.dateInPast),
        currentDate: DateF.startOfDay(currentDate),
        number: DateF.differenceInDays(currentDate, options.dateInPast),
        stepFn: DateF.subDays,
      }),

    Day: ({ number }) =>
      generateWithStep({
        dateInPast: DateF.startOfDay(options.dateInPast),
        currentDate: DateF.startOfDay(currentDate),
        number,
        stepFn: DateF.subDays,
      }),

    Hour: ({ number }) =>
      generateWithStep({
        dateInPast: DateF.startOfHour(options.dateInPast),
        currentDate: DateF.startOfHour(currentDate),
        number,
        stepFn: DateF.subHours,
        stepSize: 1,
      }),

    FifteenMinute: ({ number }) =>
      generateWithStep({
        dateInPast: roundToFifteenMinute(options.dateInPast),
        currentDate: roundToFifteenMinute(currentDate),
        number,
        stepFn: DateF.subMinutes,
        stepSize: 15,
      }),

    FiveMinute: ({ number }) =>
      generateWithStep({
        dateInPast: roundToFiveMinute(options.dateInPast),
        currentDate: roundToFiveMinute(currentDate),
        number,
        stepFn: DateF.subMinutes,
        stepSize: 5,
      }),
  })
}
